import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Neurological subsection adapter
 * Handles neurological assessment including PHQ2 scoring and seizure management (15 fields)
 */
export class NeurologicalAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Headache assessment
    const headaches = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHES
    )?.at(0)

    const headacheType = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHE_TYPE
    )

    // Seizure assessment
    const seizures = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURES
    )?.at(0)

    const seizureDate = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_DATE
    )?.at(0)

    const seizureType = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_TYPE
    )

    const seizurePrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_PRECAUTIONS
    )

    // General neurological status
    const neuroStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_NEURO_STATUS
    )

    const alteredAffect = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT
    )?.at(0)

    const alteredAffectFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT_FACTORS
    )

    // PHQ2 Depression Screening (Patient Health Questionnaire-2)
    const phq2Screening = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_SCREENING
    )?.at(0)

    const phq2Notes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_NOTES
    )?.at(0)

    // PHQ2 individual questions (scored 0-3 each)
    const interest = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_INTEREST
    )?.at(0)

    const depressed = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_DEPRESSED
    )?.at(0)

    // Caregiver assessment
    const caregiverDifficulty = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_CAREGIVER_DIFFICULTY
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [SocFormField.NEURO_HEADACHES]: headaches,
      [SocFormField.NEURO_HEADACHE_TYPE]: headacheType,
      [SocFormField.NEURO_SEIZURES]: seizures,
      [SocFormField.NEURO_SEIZURE_DATE]: seizureDate,
      [SocFormField.NEURO_SEIZURE_TYPE]: seizureType,
      [SocFormField.NEURO_SEIZURE_PRECAUTIONS]: seizurePrecautions,
      [SocFormField.NEURO_STATUS]: neuroStatus,
      [SocFormField.NEURO_ALTERED_AFFECT]: alteredAffect,
      [SocFormField.NEURO_ALTERED_AFFECT_FACTORS]: alteredAffectFactors,
      [SocFormField.NEURO_PHQ2_SCREENING]: phq2Screening,
      [SocFormField.NEURO_PHQ2_NOTES]: phq2Notes,
      [SocFormField.NEURO_INTEREST]: interest,
      [SocFormField.NEURO_DEPRESSED]: depressed,
      [SocFormField.NEURO_CAREGIVER_DIFFICULTY]: caregiverDifficulty,
      [SocFormField.NEURO_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHES,
        value: this.getFormDataField(SocFormField.NEURO_HEADACHES)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURES,
        value: this.getFormDataField(SocFormField.NEURO_SEIZURES)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_NEURO_STATUS,
        value: this.getFormDataField(SocFormField.NEURO_STATUS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT,
        value: this.getFormDataField(SocFormField.NEURO_ALTERED_AFFECT)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SYSTEM_SUMMARY,
        value: this.getFormDataField(SocFormField.NEURO_SYSTEM_SUMMARY)
      }
    ]

    if (this.formDataFieldExists(SocFormField.NEURO_PHQ2_SCREENING)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_SCREENING,
        value: this.getFormDataField(SocFormField.NEURO_PHQ2_SCREENING)
      })
    }

    // Conditionally add PHQ2-related fields if they exist (they are conditionally rendered)
    if (this.formDataFieldExists(SocFormField.NEURO_SEIZURE_DATE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_DATE,
        value: this.getFormDataField(SocFormField.NEURO_SEIZURE_DATE)
      })
    }

    if (this.formDataFieldExists(SocFormField.NEURO_SEIZURE_TYPE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_TYPE,
        value: this.getFormDataField(SocFormField.NEURO_SEIZURE_TYPE)
      })
    }

    if (this.formDataFieldExists(SocFormField.NEURO_SEIZURE_PRECAUTIONS)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_PRECAUTIONS,
        value: this.getFormDataField(SocFormField.NEURO_SEIZURE_PRECAUTIONS)
      })
    }

    if (this.formDataFieldExists(SocFormField.NEURO_INTEREST)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_INTEREST,
        value: this.getFormDataField(SocFormField.NEURO_INTEREST)
      })
    }

    if (this.formDataFieldExists(SocFormField.NEURO_DEPRESSED)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_DEPRESSED,
        value: this.getFormDataField(SocFormField.NEURO_DEPRESSED)
      })
    }

    if (this.formDataFieldExists(SocFormField.NEURO_CAREGIVER_DIFFICULTY)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_CAREGIVER_DIFFICULTY,
        value: this.getFormDataField(SocFormField.NEURO_CAREGIVER_DIFFICULTY)
      })
    }

    if (this.formDataFieldExists(SocFormField.NEURO_PHQ2_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_NOTES,
        value: this.getFormDataField(SocFormField.NEURO_PHQ2_NOTES)
      })
    }

    if (this.formDataFieldExists(SocFormField.NEURO_ALTERED_AFFECT_FACTORS)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT_FACTORS,
        value: this.getFormDataField(SocFormField.NEURO_ALTERED_AFFECT_FACTORS)
      })
    }
    if (this.formDataFieldExists(SocFormField.NEURO_HEADACHE_TYPE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHE_TYPE,
        value: this.getFormDataField(SocFormField.NEURO_HEADACHE_TYPE)
      })
    }

    return mutations
  }
}
